import { DocumentData } from 'firebase-admin/firestore'

export interface Int2 {
  x: number
  y: number
}
export interface Float3 {
  x: number
  y: number
  z: number
}

export type UserSlotDataPoint = {
  // Communication Skills
  timesReceipt: number

  timesCircumplex: number
  circumplexEvents: DocumentData[]

  commEvents: DocumentData[]
  commEventsRatio: number

  timePerReflectionQuestion: DocumentData

  // Collaboration Skills
  unlockRobotEvents: DocumentData[]
  timesUnlockedRobot: number
  timesUnlockedRobotAtMap: number
  timesFailedToUnlockRobot: number
  timesFailedToUnlockRobotAtMap: number
  unlockRobotFailRate: number
  unlockRobotFailRateAtMap: number

  timeAtMapPercentage: number

  playerSharedAbility: DocumentData[]
  timesSharedAbilityToSelf: number
  timesSharedAbilityToOthers: number

  sharedResourceEvents: DocumentData[]
  timesSharedResourcesToSelf: number
  timesSharedResourcesToOthers: number

  rescuedPlayers: string[]

  timeSpentOnDronePuzzle: number

  pathInitiatedTogetherEvents: DocumentData[]
  pathInitiatedTogetherSummaries: DocumentData[]

  pathInitiatedTogetherSummariesCount: number

  // Decision Making Skills
  pathSelectedEvents: DocumentData[]
  pathSelectedSummaries: DocumentData[]
  pathSelectedScore: number
  uridiumCollectedTotal: number
  uridiumCollectedEvents: DocumentData[]
  paralyzedEvents: DocumentData[]
  timeParalyzed: number
  oxygenEfficiency: Array<{
    numberOfTanksFullWhenReturning: number
    numberOfTanksWhenLeaving: number
    numberOfTanksWhenReturning: number
    timeSpan: number
  }>

  totalNumberOfTanksFilledWhenReturning: number

  inactiveTimeInside: number
  inactiveTimeOutside: number
  timesCalledDrone: number
  timesInterruptedDrone: number
  timesSuccessfullyGotDrone: number
  timesUsedAbility: number

  timesSharedItemsToOthers?: number

  // Voice processing metrics
  dominantEmotion: string
  airtimeTotal: number
  airtimeRelativeTotal: number
  airtimeAtMap: number
  airtimeInPreplanning: number
  airtimeRelativeInPreplanning: number
  timesOverlapAirtimeInPreplanning: number
  airtimeInReflection: number
  airtimeRelativeInReflection: number
  timesOverlapAirtimeInReflection: number
  airtimeInExecution: number
  airtimeRelativeInExecution: number
  timesOverlapAirtimeInExecution: number
  timesOverlapAirtimeTotal: number
  timesTalked: number
  timesTalkedInPreplanning: number
  timesTalkedInReflection?: number
  timesTalkedInExecution?: number
  timesReceiptInPreplanning: number
  timesReceiptInReflection: number
  timesReceiptInExecution: number
  normalizedPlayerDatapoints?: Record<string, number>
  timesReceiptTotal?: number
  timesAskQuestionOpenTotal?: number
  timesAskQuestionOpenInPreplanning?: number
  timesAskQuestionOpenInReflection?: number
  timesAskQuestionOpenInExecution?: number
  timesAskQuestionClarifyConfirmTotal?: number
  timesAskQuestionClarifyConfirmInPreplanning?: number
  timesAskQuestionClarifyConfirmInReflection?: number
  timesAskQuestionClarifyConfirmInExecution?: number
  timesInformReportTotal?: number
  timesInformReportInPreplanning?: number
  timesInformReportInReflection?: number
  timesInformReportInExecution?: number
  timesAgreeSupportTotal?: number
  timesAgreeSupportInPreplanning?: number
  timesAgreeSupportInReflection?: number
  timesAgreeSupportInExecution?: number
  timesDisagreeChallengeIssueTotal?: number
  timesDisagreeChallengeIssueInPreplanning?: number
  timesDisagreeChallengeIssueInReflection?: number
  timesDisagreeChallengeIssueInExecution?: number
  timesPlanNextStepTotal?: number
  timesPlanNextStepInPreplanning?: number
  timesPlanNextStepInReflection?: number
  timesPlanNextStepInExecution?: number
  timesDelegateAssignTotal?: number
  timesDelegateAssignInPreplanning?: number
  timesDelegateAssignInReflection?: number
  timesDelegateAssignInExecution?: number
  timesAskForHelpTotal?: number
  timesAskForHelpInPreplanning?: number
  timesAskForHelpInReflection?: number
  timesAskForHelpInExecution?: number
  timesOfferHelpTotal?: number
  timesOfferHelpInPreplanning?: number
  timesOfferHelpInReflection?: number
  timesOfferHelpInExecution?: number
  timesInviteTotal?: number
  timesInviteInPreplanning?: number
  timesInviteInReflection?: number
  timesInviteInExecution?: number
  timesRoleTalkTotal?: number
  timesRoleTalkInPreplanning?: number
  timesRoleTalkInReflection?: number
  timesRoleTalkInExecution?: number
  timesAdmitMistakeTotal?: number
  timesAdmitMistakeInPreplanning?: number
  timesAdmitMistakeInReflection?: number
  timesAdmitMistakeInExecution?: number
  timesThankTotal?: number
  timesThankInPreplanning?: number
  timesThankInReflection?: number
  timesThankInExecution?: number
  timesConstructiveFeedbackTotal?: number
  timesConstructiveFeedbackInPreplanning?: number
  timesConstructiveFeedbackInReflection?: number
  timesConstructiveFeedbackInExecution?: number
  timesGoalOrientationTotal?: number
  timesGoalOrientationInPreplanning?: number
  timesGoalOrientationInReflection?: number
  timesGoalOrientationInExecution?: number
  playerReflectionAnswersRaw?: Record<string, number>
}

export interface UserSlotDataPointMap {
  [userId: string]: UserSlotDataPoint
}

export enum EventType {
  Empty = 'Empty',
  Circumplex = 'Circumplex',
  Speak = 'Speak',
  ResourceShared = 'ResourceShared',
  AbilityShared = 'AbilityShared',
  FireShared = 'FireShared',
  PathSelected = 'PathSelected',
  UnlockedRobot = 'UnlockedRobot',
  FailedToUnlockedRobot = 'FailedToUnlockedRobot',
  Paralyzed = 'Paralyzed',
  PlacedFlag = 'PlacedFlag',
  UsedMap = 'UsedMap',
  MovedFlag = 'MovedFlag',
  UridiumDeposited = 'UridiumDeposited',
  PlayerHypothermic = 'PlayerHypothermic',
  AbilityUsed = 'AbilityUsed',
  OpenedDoor = 'OpenedDoor',
  CalledDrone = 'CalledDrone',
  InterruptedDrone = 'InterruptedDrone',
  SuccessfulDroneRoute = 'SuccessfulDroneRoute',
  InactiveInside = 'InactiveInside',
  InactiveOutside = 'InactiveOutside',
  ExitedBase = 'ExitedBase',
  EnteredBase = 'EnteredBase',
  UridiumHarvested = 'UridiumHarvested',
  RescuedPlayer = 'RescuedPlayer',
  SpentTimeDronePuzzle = 'SpentTimeDronePuzzle',
  PickedUpItem = 'PickedUpItem',
  DroppedItem = 'DroppedItem',
  ActivatedItem = 'ActivatedItem',
  ReflectionQuestionCompleted = 'ReflectionQuestionCompleted',
  InitiatedRobotUnlock = 'InitiatedRobotUnlock',
  StartedInactiveInside = 'StartedInactiveInside',
  StartedInactiveOutside = 'StartedInactiveOutside',
  EndInGame = 'EndInGame',
  StartInGame = 'StartInGame',
  OpenSquadWindow = 'OpenSquadWindow',
  CloseSquadWindow = 'CloseSquadWindow',
  ParalyzationEnded = 'ParalyzationEnded',
  GameStateChanged = 'GameStateChanged',
  OnboardingStarted = 'OnboardingStarted',
  OnboardingCompleted = 'OnboardingCompleted'
}

export enum GameState {
  Setup = 'Setup',
  Onboarding = 'Onboarding',
  Reflection = 'Reflection',
  Preplanning = 'Preplanning',
  InGame = 'InGame'
}

export interface ItrEvent {
  type: EventType
  timeStamp: number
  userId: string
  targetUserId: string
  mapUserId: string
  robotId: string
  circumplex: Int2
  lengthTime: number
  abilityType: string
  itemType: string
  positionWorld: Float3
  sharedToUserIds: string[]
  sharedToUserOxygenLevelPercentage: number[]
  sharedToUserHealthLevelPercentage: number[]
  sharedToUserBodyTempLevelPercentage: number[]
  percentageOxygen: number
  filledOxygenTanks: number
  percentageHealth: number
  percentageWarmth: number
  paralyzeType: string
  gamePath: string
  oxygenTanks: number
  oxygenTanksFull: number
  count: number
  gameState: GameState
  atMap: boolean
  sessionId: string
}
